/**
 * Custom Testimonials slider
 * Lightweight Embla-like behaviour without external deps when Embla is unavailable.
 * Loads Embla from CDN if window.EmblaCarousel is missing, then falls back to native scroll-snap.
 */
(function () {
  var EMBA_CDN = 'https://cdn.jsdelivr.net/npm/embla-carousel@8.5.2/embla-carousel.umd.js';

  function equalizeHeights(root) {
    var cards = root.querySelectorAll('.ct-card');
    if (!cards.length) return;

    // Clear previous inline heights (including Embla loop clones)
    cards.forEach(function (card) {
      card.style.minHeight = '';
    });

    // Let layout settle, then match to tallest visible content height
    var max = 0;
    cards.forEach(function (card) {
      max = Math.max(max, card.scrollHeight);
    });

    if (max > 0) {
      cards.forEach(function (card) {
        card.style.minHeight = max + 'px';
      });
    }
  }

  function loadEmbla(callback) {
    if (window.EmblaCarousel) {
      callback(window.EmblaCarousel);
      return;
    }

    var existing = document.querySelector('script[data-ct-embla]');
    if (existing) {
      existing.addEventListener('load', function () {
        callback(window.EmblaCarousel);
      });
      return;
    }

    var script = document.createElement('script');
    script.src = EMBA_CDN;
    script.async = true;
    script.dataset.ctEmbla = 'true';
    script.onload = function () {
      callback(window.EmblaCarousel);
    };
    script.onerror = function () {
      callback(null);
    };
    document.head.appendChild(script);
  }

  function setupNative(root) {
    var viewport = root.querySelector('[data-ct-viewport]');
    var track = root.querySelector('[data-ct-track]');
    var slides = Array.prototype.slice.call(root.querySelectorAll('[data-ct-slide]'));
    var prev = root.querySelector('[data-ct-prev]');
    var next = root.querySelector('[data-ct-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-ct-dot]'));
    if (!viewport || !track || !slides.length) return;

    track.style.overflowX = 'auto';
    track.style.scrollSnapType = 'x mandatory';
    track.style.scrollBehavior = 'smooth';
    slides.forEach(function (slide) {
      slide.style.scrollSnapAlign = 'start';
    });

    function slideWidth() {
      return slides[0].getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap || 0);
    }

    function go(dir) {
      track.scrollBy({ left: dir * slideWidth(), behavior: 'smooth' });
    }

    prev && prev.addEventListener('click', function () {
      go(-1);
    });
    next && next.addEventListener('click', function () {
      go(1);
    });

    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        var target = slides[index];
        if (target) target.scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
      });
    });
  }

  function setupEmbla(root, EmblaCarousel) {
    var viewport = root.querySelector('[data-ct-viewport]');
    var prev = root.querySelector('[data-ct-prev]');
    var next = root.querySelector('[data-ct-next]');
    var dots = Array.prototype.slice.call(root.querySelectorAll('[data-ct-dot]'));
    if (!viewport || !EmblaCarousel) {
      setupNative(root);
      return;
    }

    var loop = root.getAttribute('data-ct-loop') === 'true';
    var autoplay = root.getAttribute('data-ct-autoplay') === 'true';
    var speed = parseInt(root.getAttribute('data-ct-autoplay-speed') || '5000', 10);

    var embla = EmblaCarousel(viewport, {
      loop: loop,
      align: 'start',
      skipSnaps: false,
      dragFree: false,
      containScroll: loop ? false : 'trimSnaps',
      slidesToScroll: 1,
    });

    // Re-measure after Embla finishes initial layout / loop clones
    embla.on('init', function () {
      equalizeHeights(root);
    });

    function updateDots() {
      var index = embla.selectedScrollSnap();
      dots.forEach(function (dot, i) {
        var active = i === index;
        dot.classList.toggle('is-active', active);
        if (active) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    function updateButtons() {
      if (!prev || !next) return;
      if (loop) {
        prev.disabled = false;
        next.disabled = false;
        return;
      }
      prev.disabled = !embla.canScrollPrev();
      next.disabled = !embla.canScrollNext();
    }

    prev &&
      prev.addEventListener('click', function () {
        embla.scrollPrev();
      });
    next &&
      next.addEventListener('click', function () {
        embla.scrollNext();
      });

    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        embla.scrollTo(index);
      });
    });

    embla.on('select', function () {
      updateDots();
      updateButtons();
    });
    embla.on('reInit', function () {
      equalizeHeights(root);
      updateDots();
      updateButtons();
    });

    updateDots();
    updateButtons();
    equalizeHeights(root);

    root.setAttribute('tabindex', '0');
    root.addEventListener('keydown', function (event) {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        embla.scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        embla.scrollNext();
      }
    });

    var timer = null;
    function startAutoplay() {
      if (!autoplay) return;
      stopAutoplay();
      timer = setInterval(function () {
        if (embla.canScrollNext()) embla.scrollNext();
        else embla.scrollTo(0);
      }, speed);
    }
    function stopAutoplay() {
      if (timer) clearInterval(timer);
      timer = null;
    }

    startAutoplay();
    root.addEventListener('mouseenter', stopAutoplay);
    root.addEventListener('mouseleave', startAutoplay);
    root.addEventListener('focusin', stopAutoplay);
    root.addEventListener('focusout', startAutoplay);

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () {
        equalizeHeights(root);
        embla.reInit();
      }, 150);
    });
  }

  function initSection(root) {
    if (root.dataset.ctInitialized === 'true') return;
    root.dataset.ctInitialized = 'true';
    equalizeHeights(root);
    loadEmbla(function (EmblaCarousel) {
      if (EmblaCarousel) setupEmbla(root, EmblaCarousel);
      else setupNative(root);
      equalizeHeights(root);
    });
  }

  function observeSection(root) {
    if (!('IntersectionObserver' in window)) {
      initSection(root);
      return;
    }
    /* Performance: defer Embla CDN + carousel setup until section nears viewport */
    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          io.disconnect();
          initSection(root);
        });
      },
      { rootMargin: '200px 0px' }
    );
    io.observe(root);
  }

  /**
   * Open Loox Floating Reviews modal ("Happy Customers").
   * Official method discovered in installed Loox widget script:
   *   window.LOOX.openHCFloater([productId])
   * Fallback: click existing sidebar trigger #loox-floating-widget-btn / .loox-float-toggler
   */
  function isLooxPopupReady() {
    return !!(
      window.LOOX &&
      typeof window.LOOX.openHCFloater === 'function' &&
      window.LOOX.root &&
      window.LOOX.clientId
    );
  }

  function findLooxTrigger() {
    return (
      document.getElementById('loox-floating-widget-btn') ||
      document.querySelector('.loox-float-toggler') ||
      document.querySelector('.loox-float-toggler-container .loox-float-toggler')
    );
  }

  function openLooxReviewsPopup() {
    if (isLooxPopupReady()) {
      // Homepage / non-product: no productId → store-wide reviews modal
      window.LOOX.openHCFloater();
      return true;
    }

    var trigger = findLooxTrigger();
    if (trigger) {
      trigger.click();
      return true;
    }

    return false;
  }

  function whenLooxReady(callback) {
    if (isLooxPopupReady() || findLooxTrigger()) {
      callback();
      return;
    }

    var done = false;
    var observer = null;

    function finish() {
      if (done) return;
      if (!(isLooxPopupReady() || findLooxTrigger())) return;
      done = true;
      if (observer) observer.disconnect();
      document.removeEventListener('loox:loaded', onLooxLoaded);
      callback();
    }

    function onLooxLoaded() {
      finish();
    }

    // Loox defines LOOX when its script executes; watch for its known trigger DOM too.
    observer = new MutationObserver(finish);
    observer.observe(document.documentElement, { childList: true, subtree: true });

    // Some themes/apps may dispatch this; harmless if never fired.
    document.addEventListener('loox:loaded', onLooxLoaded);

    // Also re-check when deferred Loox script tags finish loading.
    document.querySelectorAll('script[src*="loox.io"]').forEach(function (script) {
      script.addEventListener('load', finish);
    });

    /* Hard stop — never leave a document-wide observer running */
    setTimeout(function () {
      if (done) return;
      done = true;
      if (observer) observer.disconnect();
      document.removeEventListener('loox:loaded', onLooxLoaded);
    }, 8000);
  }

  function bindLooxReviewsButton(sectionRoot) {
    var scope = sectionRoot || document;
    var buttons = scope.querySelectorAll('[data-ct-open-loox-reviews]');

    buttons.forEach(function (btn) {
      if (btn.dataset.ctLooxBound === 'true') return;
      btn.dataset.ctLooxBound = 'true';

      btn.addEventListener('click', function (event) {
        event.preventDefault();

        if (openLooxReviewsPopup()) return;

        whenLooxReady(function () {
          if (!openLooxReviewsPopup() && window.Shopify && Shopify.designMode) {
            console.warn(
              '[Custom Testimonials] Loox reviews popup could not be opened. Expected window.LOOX.openHCFloater or #loox-floating-widget-btn.'
            );
          }
        });
      });
    });
  }

  function boot() {
    document.querySelectorAll('[data-ct-slider]').forEach(observeSection);
    bindLooxReviewsButton(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[data-ct-slider]');
    if (root) {
      root.dataset.ctInitialized = '';
      initSection(root);
    }
    bindLooxReviewsButton(event.target);
  });
})();
